import { useRef, useEffect } from "react";
import {
  FieldValues, Control, FieldPath, Controller,
} from "react-hook-form";
import { Label, HelperText } from "flowbite-react";
import { OTPInput } from "@/shared/components/forms/ControlledOTPInput/OTPInput";
import { cn } from "@/shared/utils/style.util";

const DEFAULT_NUMBER_QUANTITY = 6;
const DEFAULT_GROUP_LENGTH = 2;

type ControlledOTPInputProps<T extends FieldValues> = React.InputHTMLAttributes<HTMLInputElement> & {
  id: string
  allowAutoComplete?: boolean
  label: string
  isRequired?: boolean
  control: Control<T>
  name: FieldPath<T>
  length?: number
  groupLength?: number
  autoFocus?: boolean
  mustScrollOnFocus?: boolean
};

export const ControlledOTPInput = <T extends FieldValues>({
  id,
  label,
  control,
  name,
  length = DEFAULT_NUMBER_QUANTITY,
  groupLength = DEFAULT_GROUP_LENGTH,
  isRequired = false,
  autoFocus = false,
  mustScrollOnFocus = false,
}: ControlledOTPInputProps<T>) => {
  const isLongLength = length > DEFAULT_NUMBER_QUANTITY;
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = (index: number): void => {
    inputRefs.current[index]?.focus();
  };

  const scrollToInput = (index: number = 0): void => {
    inputRefs.current?.[index]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  useEffect(() => {
    if (autoFocus) focusInput(0);
  }, [autoFocus, mustScrollOnFocus]);

  useEffect(() => {
    if (mustScrollOnFocus) scrollToInput();
  }, [mustScrollOnFocus]);

  return (
    <div className="relative w-full space-y-1">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <Label
              htmlFor={id}
              className={cn(
                "flex gap-2 justify-center items-center mb-2",
                isLongLength && "short:sm:mb-0.5 short:sm:!text-xs",
              )}
            >
              {label}
              {isRequired && <span className={cn(error && "text-error")}> *</span>}
            </Label>

            <OTPInput
              {...field}
              dataTestId={id}
              maxLength={length}
              groupLength={groupLength}
            />

            {error && (
              <HelperText
                color="error"
                className={cn(
                  "absolute text-xs m-0",
                  error && "text-error",
                )}
              >
                {error.message}
              </HelperText>
            )}
          </>
        )}
      />
    </div>
  );
};
